import React, { forwardRef, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import Markdown from 'markdown-to-jsx';
// src
import GeneralDrawerWrapper from '../GeneralDrawerWrapper';
import Button from '../../components/Button';
import markdownOverrides from '../../utils/MarkdownOverrides';
// styles
import styles from './styles';
import LandlordFeeDrawer from '../../components/LandlordFeeDrawer';

export interface LibFeesSideDrawerProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  feeType: string;
  standardFee?: {
    title?: string;
    text?: string;
    propertyfee?: string;
    otherfee?: string;
  };
  feeCardsContent?: {
    text?: string;
    button?: {
      href?: string;
      label?: string;
      linkText?: string;
      target?: string;
      variant?: string;
    };
  };
  buttonTertiary: {
    href?: string;
    label?: string;
    linkText?: string;
    target?: string;
    variant?: string;
  };
}

const overrides = {
  ...markdownOverrides,
  p: {
    component: Typography,
    props: { variant: 'body1', sx: styles.paragraph }
  },
  h2: {
    component: Typography,
    props: { variant: 'bold', sx: styles.mainHeading, component: 'h2' }
  },
  h3: {
    component: Typography,
    props: { variant: 'bold', sx: styles.smallHeading, component: 'h3' }
  },
  h5: {
    component: Typography,
    props: { variant: 'bold', sx: styles.smallHeading, component: 'h5' }
  },
  hr: {
    component: Divider,
    props: { sx: styles.divider }
  },
  a: {
    component: markdownOverrides.a.component,
    props: { className: 'link-component' }
  },
  span: {
    component: Typography,
    props: { sx: styles.spanText }
  }
};

const LibFeesSideDrawer = forwardRef(
  (
    {
      title = 'Drawer title here',
      feeType = 'sell',
      standardFee = {
        title: '',
        text: '',
        propertyfee: '',
        otherfee: ''
      },
      feeCardsContent,
      buttonTertiary,
      handleClose,
      open
    }: LibFeesSideDrawerProps,
    ref
  ) => {
    const innerRef = useRef(null);

    const handleFooterBtn = () => {
      if (ref) {
        ref?.current?.handleDrawerClose();
      }

      if (innerRef) {
        handleClose();
      }
    };

    return (
      <GeneralDrawerWrapper
        ref={ref || innerRef}
        title={title}
        isOpen={open}
        handleClose={handleClose}
      >
        <Box sx={styles.drawerContainer}>
          <Markdown options={{ overrides }}>{standardFee?.[0]?.title}</Markdown>
          <LandlordFeeDrawer feeType={feeType} />
          <Typography sx={styles.feeText}>
            All fees unless otherwise stated are inclusive of VAT
          </Typography>

          {feeCardsContent ? (
            <Box>
              {feeCardsContent.map((itemX) => (
                <Box sx={styles.internalBox} key={itemX.text} mt={2}>
                  <Markdown options={{ overrides }}>{itemX?.text}</Markdown>
                  {itemX?.btnPrimary?.label.length > 1 ? (
                    <Box sx={styles.btnContainer}>
                      <Button
                        href={itemX?.btnPrimary?.href}
                        variant={itemX?.btnPrimary?.variant || 'primary'}
                      >
                        {itemX?.btnPrimary?.label}
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : null}

          <Button
            fullWidth
            variant={buttonTertiary?.variant || 'primary'}
            type="button"
            sx={styles.viewFeeBtn}
            href={buttonTertiary?.href}
          >
            {buttonTertiary?.label}
          </Button>

          <Button
            fullWidth
            variant="primary-dark"
            color="primary"
            type="button"
            sx={styles.footerBtn}
            onClick={handleFooterBtn}
          >
            Close
          </Button>
        </Box>
      </GeneralDrawerWrapper>
    );
  }
);

LibFeesSideDrawer.displayName = 'FeesSideDrawer';

export const FeesSideDrawer = React.memo(LibFeesSideDrawer);

export default FeesSideDrawer;
